// 支出のCRUD・集計関数（CONTRACTS.md §3）

import { db } from './db';
import type { Expense } from './types';

/** 指定カテゴリ・指定月（'YYYY-MM'）の支出一覧を返す */
export async function listExpensesByCategoryAndMonth(
  categoryId: string,
  yearMonth: string
): Promise<Expense[]> {
  return db.expenses
    .where('date')
    .startsWith(yearMonth)
    .and((e) => e.categoryId === categoryId)
    .toArray();
}

/** 指定カテゴリ・指定日（'YYYY-MM-DD'）の支出一覧を返す */
export async function listExpensesByCategoryAndDate(
  categoryId: string,
  date: string
): Promise<Expense[]> {
  return db.expenses
    .where('[categoryId+date]')
    .equals([categoryId, date])
    .toArray();
}

export async function getExpense(id: string): Promise<Expense | undefined> {
  return db.expenses.get(id);
}

export async function addExpense(
  input: Omit<Expense, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Expense> {
  const now = new Date().toISOString();
  const expense: Expense = {
    ...input,
    id: crypto.randomUUID(),
    createdAt: now,
    updatedAt: now,
  };
  await db.expenses.put(expense);
  return expense;
}

export async function updateExpense(
  id: string,
  input: Partial<Omit<Expense, 'id' | 'createdAt'>>
): Promise<Expense> {
  const existing = await db.expenses.get(id);
  if (!existing) {
    throw new Error(`Expense not found: ${id}`);
  }
  const updated: Expense = {
    ...existing,
    ...input,
    id: existing.id,
    createdAt: existing.createdAt,
    updatedAt: new Date().toISOString(),
  };
  await db.expenses.put(updated);
  return updated;
}

export async function deleteExpense(id: string): Promise<void> {
  await db.expenses.delete(id);
}

/** 指定カテゴリ・指定月の支出合計金額 */
export async function sumExpensesByCategoryAndMonth(
  categoryId: string,
  yearMonth: string
): Promise<number> {
  const expenses = await listExpensesByCategoryAndMonth(categoryId, yearMonth);
  return expenses.reduce((sum, e) => sum + e.amount, 0);
}

/** 指定カテゴリ・指定月の支出を日付ごとに合計する（'YYYY-MM-DD' -> 合計金額） */
export async function sumExpensesByCategoryGroupedByDate(
  categoryId: string,
  yearMonth: string
): Promise<Record<string, number>> {
  const expenses = await listExpensesByCategoryAndMonth(categoryId, yearMonth);
  const result: Record<string, number> = {};
  for (const e of expenses) {
    result[e.date] = (result[e.date] ?? 0) + e.amount;
  }
  return result;
}

/** 指定月の支出をカテゴリごとに合計する（categoryId -> 合計金額。REPORTが使用） */
export async function sumExpensesByMonthGroupedByCategory(
  yearMonth: string
): Promise<Record<string, number>> {
  const expenses = await db.expenses.where('date').startsWith(yearMonth).toArray();
  const result: Record<string, number> = {};
  for (const e of expenses) {
    result[e.categoryId] = (result[e.categoryId] ?? 0) + e.amount;
  }
  return result;
}
